import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  Container,
  Skeleton,
  Avatar,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import {
  PeopleAlt,
  HowToReg,
  PersonOff,
  TrendingUp,
  FilterAlt as FilterAltIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import configServ from "../services/config";
import { useNavigate } from "react-router-dom";
import AttendanceChart from "../Components/Chart/AttendanceChart";
import PunchLogsDashboard from "../Components/Chart/PunchLogsDashboard";
import AttendanceGraph from "./AttendanceGraph";

const Dashboards = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sampleData, setSampleData] = useState(null);
  const [currentWorkingEmployees, setCurrentWorkingEmployees] = useState(null);

  // States for Filter Dropdowns
  const [departments, setDepartments] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedSite, setSelectedSite] = useState("");

  const theme = useTheme();
  const navigate = useNavigate();
  const { user_id, company_id } = useSelector((state) => state.GlobalVariables);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await configServ.getHrDashboardData({ company_id: 1 }); // Aapka API call
      if (response.status) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Dashboard Error:", error);
    } finally {
      setTimeout(() => setLoading(false), 800); // Smooth feel ke liye
    }
  };

  // Fetch Departments
  const fetchDepartments = async () => {
    try {
      if (company_id) {
        const deptRes = await configServ.getAllDepartments(company_id);
        if (deptRes?.data) {
          setDepartments(deptRes.data);
        } else if (Array.isArray(deptRes)) {
          setDepartments(deptRes);
        }
      }
    } catch (e) {
      console.error("Error fetching departments:", e);
    }
  };

  // Fetch Sites (Filtered by type "Standard")
  const fetchSiteList = async () => {
    try {
      const result = await configServ.get_customer({ company_id });
      const filteredSites = result?.filter((item) => item.type === "Standard");
      setSiteList(filteredSites || []);
    } catch (e) {
      console.error("Error fetching site list:", e);
    }
  };

  const fetchAttendanceChartData = async () => {
    try {
      const payload = {
        user_id,
        company_id,
        filter: "last_7_days",
        ...(selectedDepartment && { department_id: selectedDepartment }),
        ...(selectedSite && { site_id: selectedSite }),
      };
      const result = await configServ.getAttendanceChart(payload);
      setSampleData(result);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchCurrentWorkingEmployeesData = async () => {
    try {
      const payload = {
        user_id,
        company_id,
        filter: "last_7_days",
        ...(selectedDepartment && { department_id: selectedDepartment }),
        ...(selectedSite && { site_id: selectedSite }),
      };
      const result = await configServ.currentWorkingEmployees(payload);
      setCurrentWorkingEmployees(result);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchDepartments();
    fetchSiteList();
  }, []);

  // Filter selection change par chart update trigger hoga
  useEffect(() => {
    fetchAttendanceChartData();
    fetchCurrentWorkingEmployeesData();
  }, [selectedDepartment, selectedSite]);

  // Future-proof Array: Nayi cheezein yahan add karein
  const dashboardWidgets = [
    {
      title: "Total Employees",
      value: stats?.total_employees || 0,
      icon: <PeopleAlt sx={{ fontSize: 32 }} />,
      color: "#1976d2",
      bg: "#e3f2fd",
      path: '/employeepro/employee', // Future proof path
    },
    {
      title: "Present Today",
      value: stats?.present_today || 0,
      icon: <HowToReg sx={{ fontSize: 32 }} />,
      color: "#2e7d32",
      bg: "#e8f5e9",
      path: '/employeepro/attendance', // Future proof path
    },
    {
      title: "Absent Today",
      value: stats?.absent_today || 0,
      icon: <PersonOff sx={{ fontSize: 32 }} />,
      color: "#d32f2f",
      bg: "#ffebee",
      path: '/employeepro/attendance', // Future proof path
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {loading
          ? // Skeleton Loaders while fetching
          [1, 2, 3].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Skeleton
                variant="rounded"
                height={140}
                sx={{ borderRadius: 4 }}
              />
            </Grid>
          ))
          : // Actual Dynamic Widgets
          dashboardWidgets.map((widget, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                onClick={() => navigate(widget.path)}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  transition: "0.3s",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                  },
                  border: `1px solid ${widget.bg}`,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ fontWeight: "bold", color: "text.secondary" }}
                    >
                      {widget.title}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, my: 1 }}>
                      {widget.value}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: widget.bg,
                      color: widget.color,
                      width: 60,
                      height: 60,
                      borderRadius: 3,
                    }}
                  >
                    {widget.icon}
                  </Avatar>
                </Stack>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Filters Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mt: 4,
          mb: 1,
          borderRadius: "12px",
          border: "1px solid #e0e3ec",
          bgcolor: "#ffffff",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterAltIcon sx={{ color: "#1976d2" }} />
              <Typography variant="subtitle1" fontWeight={600} color="#1a1f36">
                Filter Analytics
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={4} md={4.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="site-select-label">Select Site</InputLabel>
              <Select
                labelId="site-select-label"
                id="site-select"
                value={selectedSite}
                label="Select Site"
                onChange={(e) => setSelectedSite(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  bgcolor: "#f8f9fa",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e3ec",
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Sites</em>
                </MenuItem>
                {siteList.map((site) => (
                  <MenuItem key={site.id || site.site_id} value={site.id || site.site_id}>
                    {site.contact_name || site.contact_name || site.contact_name || `Site ${site.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Department Dropdown */}
          <Grid item xs={12} sm={4} md={4.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="department-select-label">Select Department</InputLabel>
              <Select
                labelId="department-select-label"
                id="department-select"
                value={selectedDepartment}
                label="Select Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  bgcolor: "#f8f9fa",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e3ec",
                  },
                }}
              >
                <MenuItem value="">
                  <em>All Departments</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id || dept.department_id} value={dept.id || dept.department_id}>
                    {dept.department_name || dept.name || `Department ${dept.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

        </Grid>
      </Paper>

      {/* Attendance chart + Live punch logs */}
      <Grid container spacing={3} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: "100%" }}>
            <AttendanceChart apiResponse={sampleData} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: "100%" }}>
            <PunchLogsDashboard apiResponse={currentWorkingEmployees} />
          </Box>
        </Grid>
      </Grid>
      <AttendanceGraph />
    </Container>
  );
};

export default Dashboards;